/**
 * The scene, loaded on first use (D210).
 *
 * @remarks
 * three.js is close to the size of the whole app, and a game that never
 * offers the scene must never pay for it — so `@orpheus/player-scene` is
 * imported DYNAMICALLY, the first time the map dialog draws a `scene`
 * layout, and Vite splits it into a chunk of its own. The size pin
 * (`scripts/check-scene-split.mjs`) proves the main bundle holds none of
 * it. Until the chunk arrives the panel shows one line; if it never does
 * (a network that refused it), the line stays and the text map is a
 * directive away.
 *
 * Whether the browser can draw a scene at all is a different question, asked
 * BEFORE the style is laid out (`isWebGlAvailable`, a probe with no three.js
 * in it) — where it cannot, the shell lays the hologram out instead and the
 * panel says so, so this module never mounts a renderer that would throw.
 *
 * @packageDocumentation
 */

import { type SceneMapLayout, usePrefersReducedMotion } from '@orpheus/player-ui';
import { lazy, type ReactNode, Suspense, useMemo } from 'react';

/** What the panel shows while the renderer's chunk is on its way. */
export const SCENE_LOADING_LINE = 'Loading the scene…';

const LazySceneMapFigure = lazy(async () => {
	const { SceneMapFigure } = await import('@orpheus/player-scene');

	return { default: SceneMapFigure };
});

/**
 * D211: the surface's accent, read off the document — `--color-accent`, the
 * one saturated color the surface spends (the brand's green on the
 * terminal, the island's gold on the chart). The hologram reads it from
 * CSS on its own; the scene draws in WebGL and needs the number. Read once
 * per figure: the surface is dressed at boot (D204) and does not change
 * mid-session. Empty where no stylesheet applies (a test), and the package
 * falls back to its own default.
 *
 * @returns The accent as the stylesheet wrote it, or nothing
 */
const readDocumentAccent = (): string | undefined => {
	const accent = getComputedStyle(document.documentElement).getPropertyValue('--color-accent').trim();

	return accent === '' ? undefined : accent;
};

/** The figure with the player's motion preference read from the document — the same query the plate reads — and the material resolved: the pack's tint, else the surface's accent. */
const SceneFigure = ({ layout, title }: { readonly layout: SceneMapLayout; readonly title: string }) => {
	const isReducedMotion = usePrefersReducedMotion();
	const tint = useMemo(() => layout.tint ?? readDocumentAccent(), [layout.tint]);

	return <LazySceneMapFigure isReducedMotion={isReducedMotion} layout={layout} tint={tint} title={title} />;
};

/**
 * Draw a scene layout — the panel's render slot.
 *
 * @param layout - The engine's scene layout, tagged and tinted
 * @param title - What the pack calls its map
 * @returns The figure, behind a one-line fallback while its chunk loads
 */
export const renderSceneFigure = (layout: SceneMapLayout, title: string): ReactNode => (
	<Suspense fallback={<p className="map-panel__scene-pending">{SCENE_LOADING_LINE}</p>}>
		<SceneFigure layout={layout} title={title} />
	</Suspense>
);
